import React, { Component } from "react";
import { Button } from "react-bootstrap";
import {
  ContainerBody,
  ContainerWrapper,
  Content,
} from "../../components/GridComponent/AdminLte";
import API from "../../services/API";
import { downloadTextFile } from "../../services/FileDownload";
import { connect } from "react-redux";
import {
  fetchEventRegistrationSummary,
} from "../../store/actions/modal/modalActions";
import "./EventCheckin.css"

/** The three tables on the page and which answers each one shows. */
export const TABS = [
  { key: "registered", label: "Registered", filter: (row) => row.attending },
  { key: "checked_in", label: "Checked in", filter: (row) => row.attending && !!row.checked_in_at },
  { key: "not_attending", label: "Not attending", filter: (row) => !row.attending },
];

/** Case-insensitive match on employee ID, name or department. */
export const matchesSearch = (row, search) => {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return [row.emp_num, row.full_name, row.department].some(
    (value) => (value || "").toLowerCase().includes(needle)
  );
};

/** Distinct departments in the answers, sorted, for the department filter. */
export const departmentsOf = (rows) =>
  Array.from(new Set(rows.map((row) => row.department || ""))).sort((a, b) => a.localeCompare(b));

/**
 * Registration list page for event organisers:
 * /app/event-registrations/:eventKey
 *
 * Shows registration totals and downloads the full list as CSV.
 *
 * Redux manages:
 * - Registration summary
 * - Loading state
 * - Request status
 *
 * Local component state manages:
 * - CSV download progress
 * - CSV download errors
 */
class EventRegistrationList extends Component {
  constructor(props) {
    super(props);
  }

  state = {
    tab: "registered",
    search: "",
    department: "", // "" = all departments
    downloading: false,
    downloadError: false,
  };

  componentDidMount() {
    this.fetchSummary();
  }

  componentDidUpdate(prevProps) {
    const previousKey =
      (prevProps.params && prevProps.params.eventKey) || "";

    const currentKey =
      (this.props.params && this.props.params.eventKey) || "";

    if (previousKey !== currentKey) {
      this.fetchSummary();
    }
  }

  /**
   * Get the event key from the route parameters.
   */
  eventKey = () =>
    encodeURIComponent(
      (this.props.params && this.props.params.eventKey) || ""
    );

  /**
   * Fetch the registration summary through Redux.
   */
  fetchSummary = () => {
    this.props.fetchEventRegistrationSummary(this.eventKey());
  };

  /**
   * Download the registration list as CSV.
   */
  download = async () => {
    if (this.state.downloading) {
      return;
    }

    this.setState({
      downloading: true,
      downloadError: false,
    });

    try {
      const result = await API.call({
        method: "get",
        url:
          "/event_registration/" +
          this.eventKey() +
          "/export",
      });

      downloadTextFile(
        result.data,
        "registration-list-" +
          new Date().toISOString().slice(0, 10) +
          ".csv"
      );
    } catch (error) {
      this.setState({
        downloadError: true,
      });
    } finally {
      this.setState({
        downloading: false,
      });
    }
  };

  renderTable(rows) {
    const { tab } = this.state;
    if (rows.length === 0) {
      return <p className="text-muted" data-testid="event-registration-empty">No one here yet.</p>;
    }
    return (
      <div style={{ overflowX: "auto" }}>
        <table className="table table-sm table-striped" data-testid="event-registration-rows">
          <thead>
            <tr>
              <th>#</th>
              <th>Employee Number</th>
              <th>Name</th>
              <th>Department</th>
              <th>{tab === "not_attending" ? "Answered" : "Registered"}</th>
              {tab !== "not_attending" && <th>Checked in</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={row.emp_num + "-" + i}>
                <td>{i + 1}</td>
                <td>{row.emp_num}</td>
                <td>{row.full_name}</td>
                <td>{row.department}</td>
                <td>{row.registered_at}</td>
                {tab !== "not_attending" && <td>{row.checked_in_at || "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  /**
   * Render the registration summary and download controls.
   */
  renderBody() {
    const {
      loading,
      summary,
      rows: allRows,
      status
    } = this.props;

    console.log(loading, summary, allRows, status);

    const { tab, search, department, downloading, downloadError } = this.state;

    if (loading) {
      return <p>Loading…</p>;
    }

    if (status === null) {
      return null;
    }

    if (status === "not_authorized") {
      return (
        <div className="alert alert-danger">
          You are not authorized to view this list.
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="alert alert-warning">
          Something went wrong. Please refresh the page.
        </div>
      );
    }

    if (!summary) {
      return (
        <div className="alert alert-warning">
          Registration summary is unavailable. Please refresh
          the page.
        </div>
      );
    }

    const rows = department ? allRows.filter((row) => (row.department || "") === department) : allRows;
    const current = TABS.find((t) => t.key === tab);
    const visible = rows.filter(current.filter).filter((row) => matchesSearch(row, search));

    return (
      <>
        <div className="d-flex flex-wrap align-items-center mb-2" style={{ gap: "0.5rem" }}>
          <span className="text-muted small">As of {summary.as_of}</span>
          <Button variant="outline-secondary" size="sm" onClick={this.fetchSummary}>Refresh</Button>
          <Button variant="primary" size="sm" disabled={downloading} onClick={this.download}>
            {downloading ? "Preparing…" : "Download list (Excel / CSV)"}
          </Button>
        </div>
        {downloadError && <p className="text-danger">The download failed. Please try again.</p>}

        <select
          className="form-control mb-2"
          style={{ maxWidth: "360px" }}
          aria-label="Department"
          data-testid="event-registration-department"
          value={department}
          onChange={(e) => this.setState({ department: e.target.value })}
        >
          <option value="">All departments</option>
          {departmentsOf(allRows).map((name) => (
            <option key={name} value={name}>{name || "(No department)"}</option>
          ))}
        </select>

        <ul className="nav nav-pills mb-2" data-testid="event-registration-tabs">
          {TABS.map((t) => (
            <li className="nav-item" key={t.key}>
              <button
                type="button"
                className={"nav-link btn btn-link" + (t.key === tab ? " active" : "")}
                onClick={() => this.setState({ tab: t.key })}
              >
                {t.label} ({rows.filter(t.filter).length})
              </button>
            </li>
          ))}
        </ul>

        <input
          type="search"
          className="form-control mb-2"
          style={{ maxWidth: "360px" }}
          placeholder="Search name or employee number"
          value={search}
          onChange={(e) => this.setState({ search: e.target.value })}
        />

        {this.renderTable(visible)}
      </>
    );
  }

  render() {
    const { summary } = this.props;
    return (
      <ContainerWrapper>
        <ContainerBody>
          <Content col="12" title={summary ? summary.title + " – Registrations" : "Event Registrations"}>
            {this.renderBody()}
          </Content>
        </ContainerBody>
      </ContainerWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  summary: state.modal.registration_summary,
  rows: state.modal.registration_rows,
  loading: state.modal.registration_loading,
  status: state.modal.registration_status,
});

const mapDispatchToProps = {
  fetchEventRegistrationSummary,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventRegistrationList);